import React, { useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import { UploadService } from '../../services/uploadService';
import { Documento } from '../../types';

interface DocumentViewerProps {
  isOpen: boolean;
  onClose: () => void;
  documents: Documento[];
  title?: string;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  isOpen,
  onClose,
  documents: rawDocuments,
  title = 'Documentos'
}) => {
  // Normalizar documentos (converter string JSON para array se necessário)
  const normalizeDocuments = (docs: any): Documento[] => {
    if (!docs) return [];
    if (Array.isArray(docs)) return docs;
    if (typeof docs === 'string') {
      try {
        const parsed = JSON.parse(docs);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        console.error('Erro ao converter documentos:', e);
        return [];
      }
    }
    return [];
  };

  const documents = normalizeDocuments(rawDocuments).filter(doc => {
    // Filtrar documentos com upload falhado ou sem URL válida
    const uploadFailed = (doc as any).uploadSucesso === false;
    const hasValidUrl = doc.url || doc.storagePath || (doc as any).databaseKey;
    return !uploadFailed && hasValidUrl;
  });

  const [selectedDocument, setSelectedDocument] = useState<Documento | null>(null);
  const [loading, setLoading] = useState(false);

  const handleDownload = async (doc: Documento) => {
    try {
      setLoading(true);
      
      // Usar databaseKey como storagePath quando storagePath não existe
      const storagePath = doc.storagePath || (doc as any).databaseKey;
      
      // Verificar se o arquivo tem URL válida (nova ou antiga)
      const hasValidUrl = doc.url || storagePath;
      const uploadFailed = (doc as any).uploadSucesso === false;
      
      if (!hasValidUrl || uploadFailed) {
        alert('Arquivo não disponível. O upload falhou ou não foi realizado.');
        setLoading(false);
        return;
      }
      
      // Usar URL direta se disponível, senão gerar do storagePath
      // Determinar o bucket correto baseado no storagePath
      let bucket = 'documentos';
      if (storagePath && storagePath.startsWith('pedido-')) {
        bucket = 'pedidos';
      }
      
      const publicUrl = doc.url || UploadService.getPublicUrl(storagePath, bucket);
      
      // Fazer download via fetch para forçar o navegador a baixar
      try {
        const response = await fetch(publicUrl);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Criar link de download
        const link = window.document.createElement('a');
        link.href = blobUrl;
        link.download = doc.nome || 'documento';
        
        // Adicionar ao DOM e clicar
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        
        // Limpar URL do blob
        window.URL.revokeObjectURL(blobUrl);
      } catch (fetchError) {
        console.error('Erro ao fazer fetch do arquivo:', fetchError);
        // Fallback: tentar abrir direto
        window.open(publicUrl, '_blank');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      alert('Erro ao baixar documento');
      setLoading(false);
    }
  };

  const handleView = (doc: Documento) => {
    // Usar databaseKey como storagePath quando storagePath não existe
    const storagePath = doc.storagePath || (doc as any).databaseKey;
    
    // Verificar se o arquivo tem URL válida (nova ou antiga)
    const hasValidUrl = doc.url || storagePath;
    const uploadFailed = doc.uploadSucesso === false;
    
    if (!hasValidUrl || uploadFailed) {
      alert('Arquivo não disponível. O upload falhou ou não foi realizado.');
      return;
    }
    
    // Usar URL direta se disponível, senão gerar do storagePath
    // Determinar o bucket correto baseado no storagePath
    let bucket = 'documentos';
    if (storagePath && storagePath.startsWith('pedido-')) {
      bucket = 'pedidos';
    }
    
    const publicUrl = doc.url || UploadService.getPublicUrl(storagePath, bucket);
    window.open(publicUrl, '_blank');
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <div className="space-y-4">
        {documents.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 text-lg mb-2">📄</div>
            <p className="text-gray-500">Nenhum documento anexado</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {documents.map((doc, index) => {
              const storagePath = doc.storagePath || (doc as any).databaseKey;
              return (
                <div
                  key={doc.id || `doc-${index}`}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="text-2xl">
                      {UploadService.getFileIcon(doc.nome)}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{doc.nome}</h4>
                      <div className="text-sm text-gray-500">
                        <span>Tipo: {doc.tipo || 'Desconhecido'}</span>
                        <span className="mx-2">•</span>
                        <span>Tamanho: {doc.tamanho ? UploadService.formatFileSize(doc.tamanho) : 'Não informado'}</span>
                      </div>
                      {doc.fornecedorMercadoria && (
                        <div className="text-sm text-gray-600">
                          Fornecedor: {doc.fornecedorMercadoria}
                        </div>
                      )}
                      <div className="text-xs text-gray-400">
                        Enviado em: {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString('pt-BR') : 'Data não informada'}
                      </div>
                      {doc.uploadSucesso === false && (
                        <div className="text-xs text-orange-500 font-medium">
                          ⚠️ Arquivo não disponível (upload falhou)
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleView(doc)}
                      disabled={loading || !(doc.url || storagePath) || doc.uploadSucesso === false}
                    >
                      Visualizar
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleDownload(doc)}
                      disabled={loading || !(doc.url || storagePath) || doc.uploadSucesso === false}
                      loading={loading}
                    >
                      Baixar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <div className="flex justify-end pt-4 border-t">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Fechar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentViewer;
